<script setup lang="ts">
import { company } from '~/data/company'

const { plausibleHost } = useRuntimeConfig().public
const hasSocial = Object.values(company.social).some(Boolean)
</script>

<template>
  <div>
    <p>
      Last updated: October 2026. This privacy policy explains which personal data we process when you visit this website. The German version of this policy is legally binding.
    </p>

    <h2>Controller</h2>
    <p>
      {{ company.owner }}, {{ company.name }}<br>
      {{ company.street }}, {{ company.zip }} {{ company.city }}, Germany<br>
      Phone: {{ company.phone }}<br>
      Email: <a :href="`mailto:${company.email}`">{{ company.email }}</a>
    </p>
    <h2>Hosting and server log files</h2>
    <p>
      This website is a static site hosted by
      <template v-if="company.hosting.name">
        {{ company.hosting.name }}, {{ company.hosting.address }}.
      </template>
      <template v-else>
        our hosting provider in Germany.
      </template>
      For every page request the web server processes technically necessary data: IP address, date and time, requested URL, amount of data transferred, referrer and browser/operating system details. This is required to deliver the website and keep it secure (Art. 6(1)(f) GDPR). Log files are deleted after 14 days at the latest unless needed to investigate a security incident. We have a data processing agreement with the hosting provider (Art. 28 GDPR).
    </p>

    <template v-if="hasSocial">
      <h2>Links to social networks</h2>
      <p>
        Links to our social media profiles are plain links. Data is only transferred to the respective provider once you click a link.
      </p>
    </template>

    <template v-if="plausibleHost">
      <h2>Analytics with Plausible</h2>
      <p>
        We use Plausible Analytics, self-hosted on our own server ({{ plausibleHost }}). Plausible sets no cookies and stores nothing on your device. Your IP address is processed only briefly to create an anonymous visitor count together with a daily rotating secret; it is never stored. We only see aggregated statistics such as page views, referrers, countries and device types. The legal basis is our legitimate interest in privacy-friendly usage statistics (Art. 6(1)(f) GDPR). As nothing is accessed on your device, no consent under § 25 TDDDG is required. The data never leaves our server.
      </p>
    </template>

    <h2>Contact form and email</h2>
    <p>
      When you write to us via the form or by email, we process your name, email address, optionally your company and your message in order to answer your enquiry. Processing through the contact form is based on your consent (Art. 6(1)(a) GDPR). You can withdraw this consent at any time with effect for the future by emailing {{ company.email }}; processing before withdrawal remains lawful. For enquiries by email, the legal basis is Art. 6(1)(b) GDPR for pre-contractual enquiries, otherwise (f). The message is sent to our mailbox over an encrypted connection and is not stored in a database. Consent given through the form is recorded in the message together with the sending time. We delete enquiries once they are settled and no statutory retention periods apply.
    </p>
    <p>
      To prevent abuse, the form sets a session cookie (<code>dg_csrf</code>) only once you click into it. It contains a random identifier that lets us verify the message really comes from this form (CSRF protection) and is deleted when you close your browser. This cookie is strictly necessary for the service you request (§ 25(2) no. 2 TDDDG) and therefore needs no consent. We also briefly store a non-reversible hash of your IP address to limit mass submissions.
    </p>

    <h2>Your rights</h2>
    <p>You have the right to</p>
    <ul>
      <li>access your stored data (Art. 15 GDPR),</li>
      <li>rectification (Art. 16 GDPR) and erasure (Art. 17 GDPR),</li>
      <li>restriction of processing (Art. 18 GDPR),</li>
      <li>data portability (Art. 20 GDPR),</li>
      <li>object to processing based on legitimate interests (Art. 21 GDPR).</li>
    </ul>
    <p>
      Just contact <a :href="`mailto:${company.email}`">{{ company.email }}</a>. You may also lodge a complaint with a data protection supervisory authority, for example the State Commissioner for Data Protection and Freedom of Information of Baden-Württemberg.
    </p>

    <h2>Security</h2>
    <p>The entire website is served exclusively over encrypted HTTPS.</p>
  </div>
</template>
